import { useEffect, useRef, useSyncExternalStore } from 'react';

import {
  normalizeDatadogFeatureFlagKey,
  normalizeDatadogFeatureFlagValue,
} from 'features/observability/rum/datadog-feature-flag-utils';
import { trackDatadogFeatureFlagEvaluation } from 'features/observability/rum/track-datadog-feature-flag-evaluation';

import {
  BooleanFlags,
  FlagType,
  LaunchDarklyFlag,
  NumericFlags,
  StringFlags,
  VariationFlags,
} from './flags';
import { isLaunchDarklyIdentified, subscribeToLaunchDarklyIdentify } from './identify-readiness';
import { useFlagCore } from './platform-client';
import safeFlagDefaults from './safe-flag-defaults';

export { LaunchDarklyFlag };

/**
 * True once LaunchDarkly has initialized and every requested identify has settled. Until then a
 * `useFlag` read can still be the safe default rather than the user's targeted variation. Use this
 * before acting on a flag in a way that cannot be undone.
 */
export function useLaunchDarklyIdentified() {
  return useSyncExternalStore(
    subscribeToLaunchDarklyIdentify,
    isLaunchDarklyIdentified,
    isLaunchDarklyIdentified
  );
}

export function useFlag<T>(flag: VariationFlags): T | null | undefined;
export function useFlag(flag: NumericFlags): FlagType<NumericFlags>;
export function useFlag(flag: StringFlags): FlagType<StringFlags> | undefined;
export function useFlag(flag: BooleanFlags): FlagType<BooleanFlags>;
export function useFlag(flag: LaunchDarklyFlag): FlagType<LaunchDarklyFlag> {
  const flagValue = useFlagCore(flag, safeFlagDefaults[flag]);
  const lastTrackedValue = useRef<string | undefined>(undefined);

  useEffect(() => {
    const normalizedFlagKey = normalizeDatadogFeatureFlagKey(flag);
    const normalizedValue = normalizeDatadogFeatureFlagValue(flagValue);
    const trackedEvaluationKey = `${normalizedFlagKey}:${normalizedValue.cacheKey}`;

    if (lastTrackedValue.current === trackedEvaluationKey) {
      return;
    }

    lastTrackedValue.current = trackedEvaluationKey;
    trackDatadogFeatureFlagEvaluation(normalizedFlagKey, normalizedValue.value);
  }, [flag, flagValue]);

  return flagValue;
}
